import { GQL } from '@aline/graphql';
import { keys, pick } from 'ramda';
import type { Editor, Path, Text } from 'slate';
import { Element, Node, Point } from 'slate';

import { Queries } from '../internal';
import type {
  RenderModelInline,
  RenderModelLine,
  RenderModelLineChild,
  RenderModelParagraph,
} from '../types';
import { drawingType, isCheckboxElement, isParagraphElement, tabType } from '../types';

interface From {
  from: number;
  pageBreakWidth?: number;
}

interface FromOptions {
  from: Point;
}

interface FromTo {
  between: number;
  from: number;
  to: number;
  pageBreakWidth?: number;
}

interface FromToOptions {
  from: Point;
  to: Point;
}

type Dimensions = From | FromTo;

interface Options {
  from?: Point;
  to?: Point;
}

type DimensionsKey = keyof FromTo;

export function lineDimensions(
  editor: Editor,
  options: {
    cMParaPath: Path;
    rMLine: RenderModelLine;
    rMPara: RenderModelParagraph;
    spanOptions: FromOptions;
  }
): From;
export function lineDimensions(
  editor: Editor,
  options: {
    cMParaPath: Path;
    rMLine: RenderModelLine;
    rMPara: RenderModelParagraph;
    spanOptions: FromToOptions;
  }
): FromTo;
export function lineDimensions(
  editor: Editor,
  options: {
    cMParaPath: Path;
    rMLine: RenderModelLine;
    rMPara: RenderModelParagraph;
    spanOptions: Options;
  }
): Dimensions {
  const { cMParaPath, rMLine, rMPara, spanOptions } = options;
  const returnKeys: DimensionsKey[] = keys<Options>(spanOptions);
  const { from, to } = spanOptions;

  if (returnKeys.includes('from')) returnKeys.push('pageBreakWidth');
  if (returnKeys.includes('to')) returnKeys.push('between');

  if (from && to && Point.isAfter(from, to))
    throw new Error(`The from point ${from} cannot be after the to point ${to}.`);

  const cMParagraph = Node.get(editor, cMParaPath);
  if (!isParagraphElement(cMParagraph))
    throw new Error(`Node at path ${cMParaPath} is not a paragraph.`);

  const remainingLineWidthPx = Queries.nonJustifiedRemainingLineWidthPx(editor, {
    paraProps: rMPara,
    rMLine,
  });
  const dimensions: FromTo = {
    between: 0,
    from: 0,
    to: 0,
  };

  for (const [index, lineChild] of rMLine.children.entries()) {
    const isLastNodeInLine = index === rMLine.children.length - 1;

    resolveDimensions(editor, {
      cMParaPath,
      dimensions,
      inlineParent: null,
      isLastNodeInLine,
      rMChildPath: [index],
      rMLine,
      rMLineChild: lineChild,
      relativeLineStartPoint: rMLine.relativeStartPoint,
      remainingLineWidthPx,
      returnKeys,
      spanOptions,
    });
  }

  return pick(returnKeys, dimensions);
}

function resolveDimensions(
  editor: Editor,
  options: {
    cMParaPath: Path;
    dimensions: FromTo;
    inlineParent: RenderModelInline | null;
    isLastNodeInLine: boolean;
    relativeLineStartPoint: Point;
    remainingLineWidthPx: number;
    returnKeys: DimensionsKey[];
    // The path *in the render model line* to the child we are currently at.
    rMChildPath: Path;
    rMLine: RenderModelLine;
    rMLineChild: RenderModelLineChild;
    spanOptions: Options;
  }
): void {
  const {
    cMParaPath,
    dimensions,
    inlineParent,
    isLastNodeInLine,
    rMChildPath,
    rMLine,
    rMLineChild,
    relativeLineStartPoint,
    remainingLineWidthPx,
    returnKeys,
    spanOptions,
  } = options;
  const { from, to } = spanOptions;
  const { from: dimensionsFrom, to: dimensionsTo } = dimensions;

  if (Element.isElement(rMLineChild)) {
    for (const [index, nodeInInline] of rMLineChild.children.entries()) {
      const rMInlinePath: Path = rMChildPath.concat(index);
      const isLastNodeInInline = index === rMLineChild.children.length - 1;

      resolveDimensions(editor, {
        cMParaPath,
        dimensions,
        inlineParent: rMLineChild,
        isLastNodeInLine: isLastNodeInLine && isLastNodeInInline,
        rMChildPath: rMInlinePath,
        rMLine,
        rMLineChild: nodeInInline,
        relativeLineStartPoint,
        remainingLineWidthPx,
        returnKeys,
        spanOptions,
      });
    }
  } else {
    const measureToFromOnly = !returnKeys.includes('to');
    const isStartPathInRenderModel = rMChildPath.every(number => number === 0);
    const absoluteChildPath: Path = cMParaPath.concat(rMLineChild.relativePath);
    const lastIndex = rMLineChild.relativePath[rMLineChild.relativePath.length - 1];
    const withinPageBreak = !!inlineParent && Queries.isActivePageBreak(inlineParent);

    for (let i = 0; i <= rMLineChild.text.length; i++) {
      /**
       * Page break nodes are only supposed to contain a single, empty text node
       * so we provide this guard to be safe.
       */
      if (withinPageBreak && lastIndex !== 0) continue;

      /**
       * This point tracks the *absolute* point we are at *in the content model*.
       * We adjust the offset to account for the fact that the start of the line may be at some non-zero offset in the content model.
       */
      const point: Point = {
        offset: isStartPathInRenderModel ? i + relativeLineStartPoint.offset : i,
        path: absoluteChildPath,
      };
      const width = getTextOrInlineParentWidth(editor, {
        endOffset: i,
        inlineParent,
        isLastNodeInLine,
        rMLine,
        remainingLineWidthPx,
        textNode: rMLineChild,
      });
      const pointAfterTo = to && Point.isAfter(point, to);
      const pointAfterFrom = from && Point.isAfter(point, from);

      if (pointAfterTo || (measureToFromOnly && pointAfterFrom)) return;

      // Make sure this is after the return statement above so we don't add
      // pageBreakWidth if we are not measuring up to or past the page break.
      if (withinPageBreak && !dimensions.pageBreakWidth)
        dimensions['pageBreakWidth'] = remainingLineWidthPx;

      if (from && (Point.isBefore(point, from) || Point.equals(point, from)))
        dimensions.from = dimensionsFrom + width;

      if (to && (Point.isBefore(point, to) || Point.equals(point, to))) {
        dimensions.to = dimensionsTo + width;
        dimensions.between = dimensions.to - dimensions.from;
      }
    }
  }
}

/**
 * Utility function to get the width of text.  For some "special" nodes like
 * breaks and signature fields, it does not return the true width of child text
 * nodes but rather what we render.
 *
 * Used to determine the position of the cursor.
 */
function getTextOrInlineParentWidth(
  editor: Editor,
  {
    endOffset,
    inlineParent,
    isLastNodeInLine,
    rMLine,
    remainingLineWidthPx,
    textNode,
  }: {
    endOffset: number;
    inlineParent: RenderModelInline | null;
    isLastNodeInLine: boolean;
    remainingLineWidthPx: number;
    rMLine: RenderModelLine;
    textNode: Text;
  }
): number {
  const textWidth = Queries.getTextWidthPx(editor, {
    isLastNodeInLine,
    offset: { end: endOffset, start: 0 },
    rMLine,
    textNode,
  });

  if (!inlineParent) return textWidth;
  if (Queries.isActivePageBreak(inlineParent)) return remainingLineWidthPx;
  if (isCheckboxElement(inlineParent)) {
    return Queries.renderedCheckboxSizePx(inlineParent.children.at(0) ?? {});
  }

  switch (inlineParent.type) {
    case 'INPUT': {
      if (inlineParent.variant !== GQL.FieldVariant.Signature) return textWidth;

      const { imgWidthPx } = Queries.getSignatureImageDimensions(inlineParent);
      return imgWidthPx;
    }
    case drawingType: {
      const { containerWidthPx } = Queries.getDrawingDimensions(inlineParent);
      return containerWidthPx;
    }
    case tabType:
      return inlineParent.tabWidthPx;
    default:
      return textWidth;
  }
}
